// Artifact cards: what the Director makes, shown in the chat as it's made (frame, clip, audio, script, render).
// Every button calls the same endpoint as the Director's tool, so "approve" by voice and by tap are one action.
import { useState } from "react";
import { approveArtifact, playArtifact, redoArtifact, useVoice, type PaidOutcome } from "../actions";
import { goWords, redoStep, repriced, type StepPrice } from "../price";
import { framePoster, projectAspect } from "../session";
import { useStore } from "../store";
import type { Artifact } from "../types";
import { Icon, Sheet, Spinner } from "./bits";
import { Notice } from "./Notice";
import { usePrice } from "./PriceFirst";

const KIND: Record<Artifact["kind"], { word: string; icon: string }> = {
  frame: { word: "Frame", icon: "image" },
  clip: { word: "Clip", icon: "film" },
  audio: { word: "Sound", icon: "music" },
  script: { word: "Script", icon: "script" },
  render: { word: "Final cut", icon: "film" },
};

function kicker(a: Artifact) {
  if (a.kind === "audio" && a.voiceId) return a.voiceFor === "director" ? "Director voice" : "Narrator voice";
  const w = a.kind === "audio" && !a.sceneId ? "Music" : KIND[a.kind].word;
  return a.sceneIndex ? `Scene ${a.sceneIndex} · ${w}` : w;
}

export function StatusPill({ status, progress }: { status?: Artifact["status"]; progress?: number }) {
  if (!status) return null;
  const label = status === "working" ? (progress != null && progress > 0 ? `Making ${Math.round(progress * 100)}%` : "Making") : status === "draft" ? "Needs your OK" : status === "approved" ? "Approved" : "Failed";
  const tone = status === "working" ? "busy" : status === "approved" ? "done" : status === "failed" ? "bad" : "draft";
  return <span className={`status status-${tone} card-status`}>{label}</span>;
}

/** The waveform glyph on audio cards: fixed bars, seeded by the title so each card looks a little different. */
function Wave({ seed, on }: { seed: string; on: boolean }) {
  let h = 7;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const bars = Array.from({ length: 28 }, (_, i) => {
    h = (h * 1664525 + 1013904223) >>> 0;
    const env = Math.sin((i / 27) * Math.PI) * 0.7 + 0.3;
    return 4 + Math.round(((h >>> 8) % 100) / 100 * 18 * env);
  });
  return (
    <svg className={`card-wave${on ? " is-on" : ""}`} viewBox="0 0 112 24" preserveAspectRatio="none" aria-hidden>
      {bars.map((b, i) => (
        <rect key={i} x={i * 4} y={12 - b / 2} width="2.2" height={b} rx="1.1" />
      ))}
    </svg>
  );
}

export function ArtifactCard({ a, k, compact = false }: { a: Artifact; k: string; compact?: boolean }) {
  const playing = useStore((s) => s.playing === k);
  const [redo, setRedo] = useState(false);
  const [busy, setBusy] = useState(false);
  const working = a.status === "working";
  const actions = working ? [] : (a.actions ?? defaultActions(a)).filter((x) => !(x === "approve" && a.status === "approved"));
  const aspect = projectAspect(a.projectId).replace(":", "x");
  const visual = a.kind === "frame" || a.kind === "clip" || a.kind === "render";
  const poster = a.kind === "frame" ? undefined : framePoster(a.projectId, a.sceneId);
  const run = (f: () => Promise<unknown>) => async () => {
    setBusy(true);
    try {
      await f();
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className={`card card-k-${a.kind} card-s-${a.status ?? "plain"}${visual ? ` card-visual card-a${aspect}` : ""}${compact ? " card-compact" : ""}`} aria-label={a.title}>
      {visual && (
        <div className={`frame frame-${aspect} card-media${a.url ? " has-image" : ""}`}>
          {a.url && a.kind === "frame" && <img src={a.url} alt={a.title} />}
          {a.url && a.kind !== "frame" && <video src={a.url} poster={poster} playsInline muted preload="none" />}
          {a.url && a.kind !== "frame" && (
            <button data-action="artifact.play" type="button" className="card-play" onClick={() => playArtifact(a, k)} aria-label={`Play ${a.title}`}>
              <Icon name="play" size={22} />
            </button>
          )}
          {/* a clip in the works shows the frame it's animating */}
          {!a.url && poster && <img src={poster} alt="" className="is-dim" />}
          {!a.url && !poster && <span className="frame-wait">{working ? (a.note ?? "Working on it") : a.status === "failed" ? "Didn't work" : "Not made yet"}</span>}
          {working && <span className="frame-scan" aria-hidden />}
          <i className="corner tl" />
          <i className="corner tr" />
          <i className="corner bl" />
          <i className="corner br" />
        </div>
      )}

      <div className="card-body">
        <header className="card-head">
          <span className="card-kicker">
            <Icon name={KIND[a.kind].icon} size={15} /> {kicker(a)}
          </span>
          <StatusPill status={a.status} progress={a.progress} />
        </header>
        <h3 className="card-title">{a.title}</h3>

        {a.kind === "audio" && (
          <button data-action="artifact.play" type="button" className={`card-player${playing ? " is-on" : ""}`} onClick={() => playArtifact(a, k)} disabled={!a.url} aria-label={playing ? `Pause ${a.title}` : `Play ${a.title}`}>
            <span className="card-audio-btn">
              <Icon name={playing ? "pause" : "play"} size={18} />
            </span>
            <Wave seed={a.title + (a.voiceId ?? "")} on={playing} />
          </button>
        )}
        {a.kind === "audio" && a.text && <p className="card-quote">“{a.text}”</p>}

        {a.kind === "script" && a.text && (
          <ol className="card-script">
            {a.text
              .split(/\n+/)
              .filter(Boolean)
              .slice(0, compact ? 3 : 6)
              .map((t, i) => (
                <li key={i}>{t.replace(/^\d+[.)]\s*/, "")}</li>
              ))}
          </ol>
        )}

        {working && (
          <div className="bar card-bar" role="progressbar" aria-valuenow={Math.round((a.progress ?? 0) * 100)} aria-valuemin={0} aria-valuemax={100} aria-label={`${a.title} progress`}>
            <span style={{ width: `${Math.max(4, Math.round((a.progress ?? 0) * 100))}%` }} />
          </div>
        )}
        {a.note && (working || a.status === "failed") && <p className={`card-note${a.status === "failed" ? " is-bad" : ""}`}>{a.note}</p>}

        {!!actions.length && (
          <div className="card-actions">
            {actions.includes("use_voice") && (
              <button data-action="voice.use" type="button" className="btn btn-primary btn-small" disabled={busy} onClick={run(() => useVoice(a))}>
                <Icon name="check" size={16} /> Use this voice
              </button>
            )}
            {actions.includes("approve") && (
              <button data-action="artifact.approve" type="button" className="btn btn-primary btn-small" disabled={busy} onClick={run(() => approveArtifact(a))}>
                <Icon name="check" size={16} /> Approve
              </button>
            )}
            {actions.includes("play") && a.kind !== "audio" && a.url && (
              <button data-action="artifact.play" type="button" className="btn btn-quiet btn-small" onClick={() => playArtifact(a, k)}>
                <Icon name="play" size={14} /> Play
              </button>
            )}
            {actions.includes("redo") && (
              <button data-action="artifact.redo" type="button" className="btn btn-quiet btn-small" disabled={busy} onClick={() => setRedo(true)}>
                <Icon name="redo" size={16} /> Redo
              </button>
            )}
          </div>
        )}
      </div>

      {redo && (
        <RedoSheet
          a={a}
          onClose={() => setRedo(false)}
          onRedo={async (note, expectCredits) => {
            setBusy(true);
            try {
              return await redoArtifact(a, note, expectCredits);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </article>
  );
}

function defaultActions(a: Artifact): NonNullable<Artifact["actions"]> {
  if (a.status === "failed") return ["redo"];
  if (a.kind === "audio") return a.voiceId ? ["use_voice"] : ["redo"];
  if (a.kind === "render") return ["play"];
  if (a.kind === "clip") return ["approve", "redo", "play"];
  return ["approve", "redo"];
}

const REDO_HINT: Record<Artifact["kind"], string> = {
  frame: "Warmer light, closer on the mug, no people",
  clip: "Slower push in, keep the steam rising",
  audio: "Calmer, less bright, more space",
  script: "Funnier hook, mention free delivery",
  render: "",
};

/**
 * PRICE-FIRST: the redo's own price is on the go button ("Redo it for 15 credits"), and `onRedo` gets it to send as
 * expectCredits. `credits` when the caller knows it (a clip on its picked model); otherwise the sheet asks the api for
 * the step's price. When `onRedo` returns a refusal (the price went up), the sheet shows the new price and asks again.
 */
export function RedoSheet({ a, onClose, onRedo, credits }: { a: Artifact; onClose: () => void; onRedo: (note?: string, expectCredits?: number) => void | Promise<PaidOutcome | void | unknown>; credits?: number | null }) {
  const [note, setNote] = useState("");
  const step = redoStep(a);
  // a voice sample (or anything without a step price) keeps the plain button
  const fetched = usePrice(credits === undefined && step ? a.projectId : null, credits === undefined ? step : null, a.sceneId ?? null);
  const shown: StepPrice | null | undefined = credits !== undefined ? (credits === null ? null : { step: step ?? "frame", credits, clips: a.kind === "clip" ? 1 : 0, needsYes: true }) : step ? fetched : null;
  const [now, setNow] = useState<{ price: StepPrice; text: string } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [going, setGoing] = useState(false);
  const price = now?.price ?? shown;
  const amount = price?.credits ?? undefined;
  const pricing = !!step && price === undefined;
  return (
    <Sheet title={`Redo ${a.title.replace(/^./, (c) => c.toLowerCase())}`} onClose={onClose}>
      <form
        className="sheet-body"
        onSubmit={async (e) => {
          e.preventDefault();
          setGoing(true);
          setFailure(null);
          const r = (await onRedo(note.trim() || undefined, amount)) as PaidOutcome | void | undefined;
          setGoing(false);
          if (r && typeof r === "object" && "kind" in r && r.kind === "reprice" && price) return setNow({ price: repriced(price, r.price.credits), text: r.text });
          if (r && typeof r === "object" && "kind" in r && r.kind === "error") return setFailure(r.text);
          onClose();
        }}
      >
        <label className="field">
          <span className="field-label">What should change? Optional.</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={REDO_HINT[a.kind]} />
          <span className="field-hint">Leave it empty and your Director tries a fresh take.</span>
        </label>
        {a.kind === "clip" && <p className="sheet-note">A new clip is a new video generation, so it costs credits again. Drafts are the cheap ones.</p>}
        {now && <Notice tone="warning" text={`${now.text} Go ahead?`} code="CONFIRM_REQUIRED" />}
        {failure && <Notice tone="error" text={`Couldn't start it. ${failure}`} />}
        {pricing && <Spinner label="Pricing it" />}
        <button type="submit" className="btn btn-primary btn-block" disabled={going || pricing}>
          <Icon name="redo" size={18} /> {going ? "Starting" : goWords("Redo it", amount)}
        </button>
      </form>
    </Sheet>
  );
}
